import axios from "axios";
import { API_URL } from "src/scenes/App";
import {
    getCryptoDatabasePrefix,
    getMatrixBinding,
    getMatrixInstallationId,
    markMatrixRecoveryPending,
    removeMatrixBinding,
    renewMatrixInstallationId,
    saveMatrixBinding,
    savePendingMatrixBindingMigration,
} from "./matrixStorage";

import {
    checkCryptoStoreAccountExists,
    checkCryptoStoreGeneration,
    MatrixContinuityError,
} from "./matrixContinuity";

const DEVICE_KEY_UPLOAD_ATTEMPT_LIMIT = 10;
const DEVICE_KEY_UPLOAD_RETRY_DELAY_MS = 500;
const READINESS_ATTEMPT_LIMIT = 5;
const READINESS_RETRY_DELAYS_MS = [500, 1000, 2000, 4000];

const delay = (milliseconds) => new Promise(resolve => setTimeout(resolve, milliseconds));

export class MatrixCryptoInitializationError extends Error {
    constructor(cause) {
        super(cause.message);
        this.name = "MatrixCryptoInitializationError";
        this.cause = cause;
    }
}

const registrationsInFlight = new Map();

export const registerMatrixClient = (headers, userContext) => {
    const key = userContext?.uuid;
    if (!registrationsInFlight.has(key)) {
        const registration = registerMatrixClientOnce(headers, userContext).finally(() => {
            registrationsInFlight.delete(key);
        });
        registrationsInFlight.set(key, registration);
    }
    return registrationsInFlight.get(key);
};

const registerMatrixClientOnce = async (headers, userContext) => {
    const userUuid = userContext?.uuid;
    const storedBinding = getMatrixBinding(userUuid);
    let installationId = getMatrixInstallationId(userUuid);
    let cryptoStoreGeneration = storedBinding?.cryptoStoreGeneration;
    let cryptoStoreMigrationPending = storedBinding?.cryptoStoreMigrationPending === true;
    let cryptoDatabasePrefix = storedBinding
        ? getCryptoDatabasePrefix(
            storedBinding.userId,
            storedBinding.deviceId,
            !cryptoStoreGeneration || cryptoStoreMigrationPending
        )
        : null;
    if (storedBinding) {
        if (storedBinding.unusable) {
            throw new MatrixContinuityError();
        }
        if (!cryptoStoreGeneration) {
            cryptoStoreGeneration = crypto.randomUUID();
            await checkCryptoStoreAccountExists(cryptoDatabasePrefix);
            cryptoStoreMigrationPending = true;
            savePendingMatrixBindingMigration(userUuid, storedBinding, cryptoStoreGeneration);
        } else if (cryptoStoreMigrationPending) {
            await checkCryptoStoreAccountExists(cryptoDatabasePrefix);
        } else {
            await checkCryptoStoreGeneration(cryptoDatabasePrefix, cryptoStoreGeneration, false);
        }
    }
    cryptoStoreGeneration ||= crypto.randomUUID();
    const requestRegistration = () => axios.post(
        `${API_URL}/chats/registration`,
        {
            installationId,
            cryptoStoreGeneration,
            deviceId: storedBinding?.deviceId || "",
            deviceName: navigator.userAgent,
            proxyToken: storedBinding?.proxyToken || "",
        },
        headers
    );
    let response;
    try {
        response = await requestRegistration();
    } catch (error) {
        // No local credentials means there is no device here that can safely be resumed.
        // Preserve the old server registration; provision a separate installation once.
        if (storedBinding || error.response?.status !== 409 ||
            error.response?.data?.errorCode !== "CRYPTO_STORE_MISMATCH") {
            throw error;
        }
        installationId = renewMatrixInstallationId(userUuid);
        response = await requestRegistration();
    }
    const session = parseMatrixSession(response.data);
    if (response.data.cryptoStoreGeneration !== cryptoStoreGeneration ||
        (storedBinding && (session.deviceId !== storedBinding.deviceId || session.userId !== storedBinding.userId))) {
        throw new MatrixContinuityError();
    }
    if (getMatrixBinding(userUuid)?.unusable) {
        throw new MatrixContinuityError();
    }
    cryptoDatabasePrefix ||= getCryptoDatabasePrefix(session.userId, session.deviceId, false);
    if (cryptoStoreMigrationPending) {
        savePendingMatrixBindingMigration(userUuid, session, cryptoStoreGeneration);
    } else {
        saveMatrixBinding(userUuid, session, cryptoStoreGeneration);
    }
    return {
        installationId,
        session,
        cryptoStoreGeneration,
        cryptoDatabasePrefix,
        cryptoStoreMigrationPending,
        isNew: !storedBinding,
    };
};

export const initializeMatrixEncryptionReadiness = async (
    client,
    cryptoDatabasePrefix,
    afterCryptoInitialized,
    headers,
    identifiers,
    options
) => {
    try {
        await client.initRustCrypto({ cryptoDatabasePrefix });
    } catch (error) {
        throw new MatrixCryptoInitializationError(error);
    }
    await afterCryptoInitialized();
    await confirmMatrixDeviceKeysUploaded(client, options);
    await reportMatrixRegistrationReady(headers, identifiers, options);
};

export const confirmMatrixDeviceKeysUploaded = async (client, options) => {
    const crypto = client.getCrypto?.();
    const userId = client.getUserId?.();
    const deviceId = client.getDeviceId?.();
    if (!crypto || !userId || !deviceId) {
        throw new Error("Matrix encryption is not initialized for this device.");
    }

    const ownKeys = await crypto.getOwnDeviceKeys();
    const attemptLimit = options?.deviceKeyAttemptLimit || DEVICE_KEY_UPLOAD_ATTEMPT_LIMIT;
    const retryDelayMs = options?.deviceKeyRetryDelayMs ?? DEVICE_KEY_UPLOAD_RETRY_DELAY_MS;
    const wait = options?.delay || delay;

    for (let attempt = 0; attempt < attemptLimit; attempt += 1) {
        try {
            const response = await client.downloadKeysForUsers([userId], {});
            const uploadedKeys = response?.device_keys?.[userId]?.[deviceId]?.keys;
            if (
                uploadedKeys?.[`ed25519:${deviceId}`] === ownKeys.ed25519 &&
                uploadedKeys?.[`curve25519:${deviceId}`] === ownKeys.curve25519
            ) {
                return;
            }
        } catch (error) {
            if (!isTransientMatrixError(error)) {
                throw error;
            }
        }

        if (attempt + 1 < attemptLimit) {
            await wait(retryDelayMs);
        }
    }

    throw new Error("Matrix device keys were not confirmed on the homeserver.");
};

export const reportMatrixRegistrationReady = async (headers, identifiers, options) => {
    const attemptLimit = options?.readinessAttemptLimit || READINESS_ATTEMPT_LIMIT;
    const retryDelays = options?.readinessRetryDelaysMs || READINESS_RETRY_DELAYS_MS;
    const wait = options?.delay || delay;

    for (let attempt = 0; attempt < attemptLimit; attempt += 1) {
        try {
            await axios.post(`${API_URL}/chats/registration/ready`, identifiers, headers);
            return;
        } catch (error) {
            if (!isTransientApiError(error) || attempt + 1 >= attemptLimit) {
                throw error;
            }
            await wait(retryDelays[Math.min(attempt, retryDelays.length - 1)]);
        }
    }
};

const isTransientStatus = (status) => {
    return !status || status === 408 || status === 425 || status === 429 || status >= 500;
};

const isTransientApiError = (error) => isTransientStatus(error.getStatus?.() ?? error.response?.status);

const isTransientMatrixError = (error) => isTransientStatus(error?.httpStatus);

const parseMatrixSession = (responseData) => {
    const session = responseData?.session;
    if (
        typeof session?.userId !== "string" ||
        typeof session?.deviceId !== "string" ||
        typeof session?.token !== "string"
    ) {
        throw new Error("Invalid Matrix registration response.");
    }
    return session;
};

// Call only from a recovery flow with login recovery material or explicit user
// confirmation. Retain old databases as archives; a fresh device uses a new store.
export const resetMatrixInstallation = async (headers, userUuid) => {
    const binding = getMatrixBinding(userUuid);
    if (!binding) {
        throw new MatrixContinuityError();
    }
    const response = await axios.post(`${API_URL}/chats/registration/reset`, {
        installationId: getMatrixInstallationId(userUuid),
        deviceId: binding.deviceId,
        recoveryConfirmed: true,
    }, headers);
    if (response.data.retired !== true) {
        throw new MatrixContinuityError();
    }
    if (getMatrixBinding(userUuid)?.deviceId !== binding.deviceId) {
        throw new MatrixContinuityError();
    }
    markMatrixRecoveryPending(userUuid);
    removeMatrixBinding(userUuid);
};

export const migrateMatrixInstallationOnLogin = async (headers, userContext) => {
    await resetMatrixInstallation(headers, userContext?.uuid);
    return registerMatrixClient(headers, userContext);
};
